import React from "react";
import GenericListPage from "@/pages/GenericListPage";
import { useT } from "@/context/I18nContext";
import { Users2, MapPin, LayoutGrid, ScrollText, MailPlus, HeartHandshake } from "lucide-react";

export function PlatformPeople() {
  const t = useT();
  return (
    <GenericListPage
      testId="pf-people"
      eyebrow={t("nav.business")}
      title={t("nav.people")}
      subtitle={t("platform.subtitle")}
      url="/platform/people"
      emptyIcon={Users2}
      searchFields={["name", "email", "role"]}
      renderItem={(u, i) => (
        <div key={u.id || u.user_id || i} className="rounded-2xl bg-white shadow-card p-4">
          <div className="text-ink-900 font-medium truncate">{u.name || "—"}</div>
          <div className="text-xs text-ink-500 truncate">{u.email || ""}</div>
          <div className="text-xs text-ink-500 mt-1">
            {u.role || (Array.isArray(u.roles) ? u.roles.join(", ") : "")}
          </div>
          {u.team_name && <div className="text-xs text-sage-700 mt-1">{u.team_name}</div>}
        </div>
      )}
    />
  );
}

export function PlatformTeams() {
  const t = useT();
  return (
    <GenericListPage
      testId="pf-teams"
      eyebrow={t("nav.business")}
      title={t("nav.teams")}
      subtitle={t("platform.subtitle")}
      url="/platform/teams"
      emptyIcon={Users2}
      searchFields={["name", "title"]}
      renderItem={(u, i) => (
        <div key={u.id || i} className="rounded-2xl bg-white shadow-card p-4">
          <div className="text-ink-900 font-medium truncate">{u.name || u.title || "—"}</div>
          <div className="text-xs text-ink-500 truncate">{u.description || ""}</div>
          {(u.members_count != null || u.member_count != null) && (
            <div className="text-xs text-sage-700 mt-1">{u.members_count ?? u.member_count} members</div>
          )}
        </div>
      )}
    />
  );
}

export function PlatformLocations() {
  const t = useT();
  return (
    <GenericListPage
      testId="pf-locations"
      eyebrow={t("nav.business")}
      title={t("nav.locations")}
      subtitle={t("platform.subtitle")}
      url="/platform/locations"
      emptyIcon={MapPin}
      searchFields={["name", "city"]}
      renderItem={(u, i) => (
        <div key={u.id || i} className="rounded-2xl bg-white shadow-card p-4">
          <div className="text-ink-900 font-medium truncate">{u.name || "—"}</div>
          <div className="text-xs text-ink-500 truncate">{u.city || u.address || ""}</div>
        </div>
      )}
    />
  );
}

export function PlatformDepartments() {
  const t = useT();
  return (
    <GenericListPage
      testId="pf-departments"
      eyebrow={t("nav.business")}
      title={t("nav.departments")}
      subtitle={t("platform.subtitle")}
      url="/platform/departments"
      emptyIcon={LayoutGrid}
      searchFields={["name"]}
      renderItem={(u, i) => (
        <div key={u.id || i} className="rounded-2xl bg-white shadow-card p-4">
          <div className="text-ink-900 font-medium truncate">{u.name || "—"}</div>
          <div className="text-xs text-ink-500 truncate">{u.description || ""}</div>
        </div>
      )}
    />
  );
}

export function PlatformInvites() {
  const t = useT();
  return (
    <GenericListPage
      testId="pf-invites"
      eyebrow={t("nav.business")}
      title={t("nav.invites")}
      subtitle={t("platform.outbox")}
      url="/platform/invites/outbox"
      emptyIcon={MailPlus}
      searchFields={["email"]}
      renderItem={(u, i) => (
        <div key={u.id || u.token || i} className="rounded-2xl bg-white shadow-card p-4">
          <div className="text-ink-900 font-medium truncate">{u.email || "—"}</div>
          <div className="text-xs text-ink-500 truncate font-mono">{u.token || u.code || u.id || ""}</div>
          {u.status && <div className="text-xs text-sage-700 mt-1">{u.status}</div>}
        </div>
      )}
    />
  );
}

export function PlatformAudit() {
  const t = useT();
  return (
    <GenericListPage
      testId="pf-audit"
      eyebrow={t("nav.business")}
      title={t("nav.audit")}
      subtitle={t("platform.audit")}
      url="/platform/audit"
      columns="md:grid-cols-1 lg:grid-cols-2"
      emptyIcon={ScrollText}
      searchFields={["action", "actor", "target"]}
      renderItem={(a, i) => (
        <div key={a.id || i} className="rounded-2xl bg-white shadow-card p-4">
          <div className="text-xs text-ink-500">{a.created_at && new Date(a.created_at).toLocaleString()}</div>
          <div className="text-sm text-ink-900 mt-1">
            <span className="font-medium">{a.actor || a.actor_email || "system"}</span>{" · "}
            <span className="text-sage-700">{a.action || a.event}</span>{a.target ? ` → ${a.target}` : ""}
          </div>
          {a.meta && <pre className="text-xs text-ink-500 mt-1 font-mono whitespace-pre-wrap max-h-24 overflow-hidden">{JSON.stringify(a.meta, null, 2)}</pre>}
        </div>
      )}
    />
  );
}

export function PlatformAnalytics() {
  const t = useT();
  return (
    <GenericListPage
      testId="pf-analytics"
      eyebrow={t("nav.business")}
      title={t("nav.analytics")}
      subtitle={t("platform.subtitle")}
      url="/platform/analytics/team"
      columns="md:grid-cols-2"
      emptyIcon={Users2}
      extract={(d) => Array.isArray(d) ? d : (d?.teams || d?.rows || Object.entries(d || {}).map(([k, v]) => ({ name: k, value: v })))}
      searchFields={["name"]}
      renderItem={(row, i) => (
        <div key={row.id || i} className="rounded-2xl bg-white shadow-card p-4">
          <div className="text-ink-900 font-medium truncate">{row.name || row.team || "—"}</div>
          <pre className="text-xs text-ink-500 mt-1 font-mono whitespace-pre-wrap max-h-20 overflow-hidden">
            {typeof row.value === "object" ? JSON.stringify(row.value, null, 2) : JSON.stringify(row, null, 2)}
          </pre>
        </div>
      )}
    />
  );
}

export function PlatformWellbeing() {
  const t = useT();
  return (
    <GenericListPage
      testId="pf-wellbeing"
      eyebrow={t("nav.business")}
      title={t("nav.wellbeing")}
      subtitle={t("platform.wellbeingAggregate")}
      url="/org/wellbeing/users"
      emptyIcon={HeartHandshake}
      searchFields={["name", "team"]}
      renderItem={(u, i) => (
        <div key={u.id || i} className="rounded-2xl bg-white shadow-card p-4">
          <div className="text-ink-900 font-medium truncate">{u.name || u.pseudonym || `Member #${i + 1}`}</div>
          <div className="text-xs text-ink-500">{u.team || u.department || ""}</div>
          {u.score != null && <div className="text-sage-700 text-sm mt-1">Wellbeing: {u.score}</div>}
        </div>
      )}
    />
  );
}
